import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Save, CheckCircle2, Loader2, Plus, Trash2, Building2, Network } from 'lucide-react';
import { toast } from 'sonner';
import {
  onboardingAPI, RELATED_ENTITY_TYPE_OPTIONS, RELATED_ROLE_OPTIONS,
} from '../../lib/onboardingAPI';

const EMPTY = {
  entity_name: '', entity_type: 'company', abn: '', acn: '', tfn: '',
  role_of_client: 'director', entity_email: '', registered_address: '',
  business_address: '', requires_services: false,
};

const typeLabel = (v) => (RELATED_ENTITY_TYPE_OPTIONS.find((o) => o.value === v) || {}).label || v;

export default function RelatedEntitiesForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hasEntities, setHasEntities] = useState('no');
  const [entities, setEntities] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const load = useCallback(async () => {
    try {
      const res = await onboardingAPI.getSection(clientId, 'related_entities');
      const rd = res.data.response_data || {};
      const list = rd.entities || [];
      setEntities(list);
      if (typeof rd.has_related_entities === 'boolean') setHasEntities(rd.has_related_entities ? 'yes' : 'no');
      else if (list.length) setHasEntities('yes');
    } catch {
      // first visit — section not created yet
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const addEntity = async () => {
    if (!draft.entity_name.trim()) { toast.error('Entity name is required'); return; }
    setBusy(true);
    try {
      await onboardingAPI.addRelatedEntity(clientId, draft);
      toast.success('Related entity added — a linked client record was created in your group');
      setDraft(EMPTY);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add entity');
    } finally {
      setBusy(false);
    }
  };

  const removeEntity = async (entityClientId) => {
    setBusy(true);
    try {
      await onboardingAPI.removeRelatedEntity(clientId, entityClientId);
      toast.success('Entity removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove entity');
    } finally {
      setBusy(false);
    }
  };

  const finalize = async (status) => {
    setBusy(true);
    try {
      await onboardingAPI.finalizeRelatedEntities(clientId, { has_related_entities: hasEntities === 'yes', status });
      toast.success(status === 'completed' ? 'Section marked complete' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  const yes = hasEntities === 'yes';

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Related Entities</CardTitle>
          <CardDescription>Are you connected to any related entities (companies, trusts, partnerships, SMSFs)?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <RadioGroup value={hasEntities} onValueChange={setHasEntities} className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" data-testid="re-yes" /> Yes</label>
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" data-testid="re-no" /> No</label>
          </RadioGroup>

          {yes && (
            <>
              <div className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Network className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Each entity you add becomes its own linked client record in your group, with its own onboarding and documents.
              </div>

              {/* Existing entities */}
              {entities.length > 0 && (
                <div className="space-y-2">
                  {entities.map((e) => (
                    <div key={e.entity_client_id} className="flex items-center gap-3 border rounded-lg p-3" data-testid={`re-card-${e.entity_client_id}`}>
                      <Building2 className="w-5 h-5 text-foreground" />
                      <div className="flex-1">
                        <div className="font-medium text-foreground">{e.entity_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {typeLabel(e.entity_type)}{e.role_of_client ? ` · your role: ${e.role_of_client}` : ''}{e.abn ? ` · ABN ${e.abn}` : ''}
                        </div>
                      </div>
                      {e.requires_services && <Badge className="bg-green-100 text-green-800">needs services</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => removeEntity(e.entity_client_id)} disabled={busy} title="Remove">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add-entity form */}
              {showForm ? (
                <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Entity name *</Label><Input value={draft.entity_name} onChange={onInput('entity_name')} data-testid="re-name" /></div>
                    <div className="space-y-2">
                      <Label>Entity type</Label>
                      <Select value={draft.entity_type} onValueChange={set('entity_type')}>
                        <SelectTrigger data-testid="re-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{RELATED_ENTITY_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>ABN</Label><Input value={draft.abn} onChange={onInput('abn')} /></div>
                    {draft.entity_type === 'company' && (
                      <div className="space-y-2"><Label>ACN</Label><Input value={draft.acn} onChange={onInput('acn')} /></div>
                    )}
                    <div className="space-y-2"><Label>TFN (optional)</Label><Input value={draft.tfn} onChange={onInput('tfn')} /></div>
                    <div className="space-y-2">
                      <Label>Your role</Label>
                      <Select value={draft.role_of_client} onValueChange={set('role_of_client')}>
                        <SelectTrigger data-testid="re-role"><SelectValue /></SelectTrigger>
                        <SelectContent>{RELATED_ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Entity email</Label><Input type="email" value={draft.entity_email} onChange={onInput('entity_email')} /></div>
                    <div className="space-y-2"><Label>Registered address</Label><Input value={draft.registered_address} onChange={onInput('registered_address')} /></div>
                    <div className="space-y-2 md:col-span-2"><Label>Business address</Label><Input value={draft.business_address} onChange={onInput('business_address')} /></div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={draft.requires_services} onCheckedChange={(v) => set('requires_services')(!!v)} data-testid="re-requires" />
                    <span className="text-sm text-foreground/90">This entity requires tax / accounting services</span>
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => { setShowForm(false); setDraft(EMPTY); }}>Cancel</Button>
                    <Button onClick={addEntity} disabled={busy} data-testid="re-add-submit"><Plus className="w-4 h-4 mr-1" /> Add entity</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setShowForm(true)} data-testid="re-add-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add a related entity
                </Button>
              )}
            </>
          )}

          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => finalize('in_progress')} disabled={busy} data-testid="re-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => finalize('completed')} disabled={busy} data-testid="re-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
